import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, Crown, Trophy, Medal, Users, Target, TrendingUp, Minus } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Comparativa de Supervisores (solo admin) — tabla con: supervisor, equipo,
 * total renovaciones, % meta ≤20 días, promedio días digitación. Ordenada
 * por desempeño (mejor %meta primero).
 */
export default function SupervisorComparative({ mesVencimiento = "todos", onlyRenovadas = false }) {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const authHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("mes_vencimiento", mesVencimiento === "todos" ? "all" : mesVencimiento);
      if (onlyRenovadas) params.set("only_renovadas", "true");
      const r = await axios.get(
        `${API_URL}/api/estadisticas/renovaciones/comparativa-supervisores?${params.toString()}`,
        authHeaders(),
      );
      setItems(r.data?.items || []);
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error("No se pudo cargar la comparativa de supervisores");
      }
      setItems([]);
    }
    setLoading(false);
  }, [mesVencimiento, onlyRenovadas, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Encuentra el % meta más alto para destacar al "top performer"
  const topPct = items.reduce((max, r) => (r.meta_20_dias_pct ?? -1) > max ? (r.meta_20_dias_pct ?? -1) : max, -1);
  const withData = items.filter((r) => r.meta_20_dias_pct !== null && r.meta_20_dias_pct !== undefined);

  const podiumIcon = (rank) => {
    if (rank === 0) return <Crown className="w-4 h-4 text-amber-500" />;
    if (rank === 1) return <Trophy className="w-4 h-4 text-slate-400" />;
    if (rank === 2) return <Medal className="w-4 h-4 text-orange-700" />;
    return <span className="w-4 h-4 inline-flex items-center justify-center text-[10px] font-bold text-slate-400">#{rank + 1}</span>;
  };

  const metaBadge = (pct) => {
    if (pct === null || pct === undefined) {
      return <span className="text-slate-300 text-xs">—</span>;
    }
    let cls = "bg-rose-100 text-rose-700 border-rose-300";
    if (pct >= 80) cls = "bg-emerald-100 text-emerald-700 border-emerald-300";
    else if (pct >= 50) cls = "bg-amber-100 text-amber-700 border-amber-300";
    return (
      <Badge variant="outline" className={`text-[11px] px-2 py-0.5 font-bold ${cls}`}>
        <TrendingUp className="w-3 h-3 mr-1" />
        {pct}%
      </Badge>
    );
  };

  return (
    <Card className="border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="supervisor-comparative-card">
      <CardHeader className="py-3 px-4 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-rose-50 dark:from-violet-950/30 dark:via-fuchsia-950/30 dark:to-rose-950/30">
        <CardTitle className="text-sm font-bold flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-fuchsia-600" />
            <span>Comparativa entre Supervisores</span>
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-violet-100 text-violet-700 border-violet-200">SOLO ADMIN</Badge>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </CardTitle>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 ml-6">
          Desempeño en el ciclo de renovaciones por equipo · ordenado por % meta ≤20 días
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {items.length === 0 && !loading ? (
          <div className="text-center text-slate-400 py-8 text-sm">Sin supervisores para mostrar</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]" data-testid="supervisor-comparative-table">
              <thead className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left w-[40px]">#</th>
                  <th className="px-3 py-2 text-left">Supervisor</th>
                  <th className="px-3 py-2 text-center w-[80px]"><Users className="w-3 h-3 inline mr-1" />Equipo</th>
                  <th className="px-3 py-2 text-center w-[90px]">Total pólizas</th>
                  <th className="px-3 py-2 text-center w-[80px]">Renovadas</th>
                  <th className="px-3 py-2 text-center w-[80px]">Pendientes</th>
                  <th className="px-3 py-2 text-center w-[110px]">Prom. días digit.</th>
                  <th className="px-3 py-2 text-center w-[110px]"><Target className="w-3 h-3 inline mr-1" />Meta ≤20 días</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.map((r, idx) => {
                  const hasData = r.meta_20_dias_pct !== null && r.meta_20_dias_pct !== undefined;
                  const isTop = hasData && r.meta_20_dias_pct === topPct && topPct > 0;
                  // Rank only counts those with data
                  const rankWithData = hasData ? withData.findIndex((w) => w.supervisor_id === r.supervisor_id) : -1;
                  return (
                    <tr
                      key={r.supervisor_id}
                      className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${isTop ? "bg-amber-50/40 dark:bg-amber-950/20" : ""}`}
                      data-testid={`supervisor-row-${r.supervisor_id}`}
                    >
                      <td className="px-3 py-2.5">
                        {hasData ? podiumIcon(rankWithData) : <Minus className="w-3 h-3 text-slate-300" />}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 dark:text-slate-100">{r.supervisor_name}</div>
                        <div className="text-[10px] text-slate-400">{r.supervisor_email}</div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50 text-slate-600 border-slate-200">
                          {r.team_size}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-center font-mono font-bold">{r.total}</td>
                      <td className="px-3 py-2.5 text-center text-emerald-700 font-semibold">{r.renovadas}</td>
                      <td className="px-3 py-2.5 text-center text-slate-500">{r.pendientes}</td>
                      <td className="px-3 py-2.5 text-center font-semibold">
                        {r.promedio_dias_digitacion !== null ? `${r.promedio_dias_digitacion} d` : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {metaBadge(r.meta_20_dias_pct)}
                        {hasData && (
                          <div className="text-[9px] text-slate-400 mt-0.5">{r.meta_20_dias_count}/{r.meta_20_dias_total_calc}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
